import React from 'react'
import AppLoadingIndicator from 'core/components/loaders/AppLoadingIndicator'
import cn from 'classnames'
import Icon from 'core/components/icons/Icon'

export interface SkeletonsProps {
  isChildPage?: boolean
  withBreadcrumbs?: boolean
  withBanner?: boolean
  variant?:
    | 'default'
    | 'home'
    | 'team-home'
    | 'settings'
    | 'report'
    | 'pay-period'
    | 'schedule'
    | 'integrations'
    | 'billing'
    | 'premium'
    | 'teams-features'
    | 'app'
    | 'report-drawer'
    | 'about-you'
    | 'sign-up'
    | 'accept-invite'
    | 'auth'
    | 'member-tab'
    | 'trip-detail'
    | 'merge-import'
  className?: string
}

const Skeletons = ({
  variant = 'default',
  isChildPage,
  withBreadcrumbs,
  withBanner,
  className
}: SkeletonsProps) => {
  const title = <div className="skeleton h-7 w-1/3" />
  const subtitle = <div className="skeleton h-5 w-2/3" />
  const tab = <div className="skeleton h-7 w-32" />
  const button = <div className="skeleton h-10 w-36" />
  const iconButton = <div className="skeleton h-10 w-12" />
  const header = (
    <div className="flex items-top justify-between w-full">
      <div className="flex flex-col gap-5 w-full">
        {title}
        {subtitle}
      </div>
      {button}
    </div>
  )

  const breadcrumbs = (
    <div className="flex items-center gap-2">
      <div className="skeleton h-5 w-28" />
      <Icon name="right" noWrapper size={10} color="text-dark-4" />
      <div className="skeleton h-5 w-32" />
      <Icon name="right" noWrapper size={10} color="text-dark-4" />
      <div className="skeleton h-5 w-24" />
      <Icon name="right" noWrapper size={10} color="text-dark-4" />
      <div className="skeleton h-5 w-32" />
    </div>
  )

  const defaultContent = (
    <div className="w-full flex flex-col gap-10">
      {withBreadcrumbs && breadcrumbs}
      {header}
      {withBanner && <div className="skeleton h-[15rem] w-full" />}
      <div className="skeleton h-[50vh] w-full" />
    </div>
  )

  const settingsContent = (
    <div className="w-full">
      <div className=" w-full flex flex-col gap-10">
        {breadcrumbs}
        {header}
        <div className="skeleton h-[25vh] w-full" />
        <div className="skeleton h-[25vh] w-full" />
        <div className="skeleton h-[25vh] w-full" />
      </div>
    </div>
  )

  const homeContent = (
    <div className=" grid grid-cols-12 gap-5 w-full">
      <div className="col-span-12">
        <div className="skeleton h-7 w-1/6" />
      </div>

      <div className="col-span-12 ml:col-span-7 w-full flex gap-5 flex-col">
        <div className="skeleton h-[35vh] w-full" />
        <div className="skeleton h-[35vh] w-full" />
      </div>

      <div className="col-span-12 ml:col-span-5 w-full flex gap-5 flex-col">
        <div className="skeleton h-[20vh] w-full" />
        <div className="skeleton h-[35vh] w-full" />
      </div>
    </div>
  )
  const teamHomeContent = (
    <div className=" grid grid-cols-12 gap-5 w-full">
      <div className="col-span-12 flex items-center justify-between">
        <div className="skeleton h-7 w-1/6" />
        <div className="flex items-center gap-5">
          {button}
          {button}
        </div>
      </div>

      <div className="col-span-12 ml:col-span-7 w-full flex gap-5 flex-col">
        <div className="skeleton h-[35vh] w-full" />
        <div className="skeleton h-[35vh] w-full" />
      </div>

      <div className="col-span-12 ml:col-span-5 w-full flex gap-5 flex-col">
        <div className="skeleton h-[20vh] w-full" />
        <div className="skeleton h-[35vh] w-full" />
        <div className="skeleton h-[15vh] w-full" />
      </div>
    </div>
  )

  const reportContent = (
    <div className=" w-full flex flex-col gap-10">
      {withBanner && <div className="skeleton h-[15rem] w-full" />}
      {breadcrumbs}
      <div className="flex items-center justify-between">
        <div className="skeleton h-7 w-1/6" />
        <div className="flex items-center gap-5">
          {button}
          {button}
          {button}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-8 w-full flex gap-5 flex-col">
          <div className="w-full flex gap-5 flex-col md:flex-row">
            <div className="skeleton h-[25rem] w-full md:w-1/2" />
            <div className="skeleton h-[25rem] w-full md:w-1/2" />
          </div>
          <div className="skeleton h-[40vh] w-full mt-10" />
        </div>

        <div className="col-span-12 ml:col-span-4 w-full flex gap-5 flex-col">
          <div className="skeleton h-[60vh] w-full" />
        </div>
      </div>
    </div>
  )

  const reportDrawerContent = (
    <div className=" w-full flex flex-col gap-10">
      <div className="flex items-center justify-between">
        <div className="skeleton h-7 w-1/6" />
        <div className="flex items-center gap-5">
          {button}
          {button}
          {button}
        </div>
      </div>

      <div className="flex flex-col w-full gap-10">
        <div className="w-full flex gap-5 flex-col md:flex-row">
          <div className="skeleton h-[25rem] w-full md:w-1/2" />
          <div className="skeleton h-[25rem] w-full md:w-1/2" />
        </div>

        <div className="skeleton h-[60vh] w-full" />
      </div>
    </div>
  )

  const payPeriodContent = (
    <div className=" w-full flex flex-col gap-10">
      {breadcrumbs}
      <div className="flex items-center justify-between">
        <div className="skeleton h-7 w-1/6" />
        <div className="flex items-center gap-5">
          {button}
          {iconButton}
        </div>
      </div>

      <div className="col-span-12 ml:col-span-8 w-full flex gap-5 flex-col">
        <div className="skeleton h-[20vh] w-full" />
      </div>

      <div className="col-span-12 ml:col-span-4 w-full flex gap-2 flex-col">
        <div className="flex items-center gap-5 mb-3">
          {tab}
          {tab}
          {tab}
          {tab}
          {tab}
        </div>
        <div className="skeleton h-[60vh] w-full" />
      </div>
    </div>
  )

  const scheduleContent = (
    <div className=" w-full flex flex-col gap-10">
      {breadcrumbs}
      <div className="flex items-center justify-between">
        <div className="skeleton h-7 w-1/6" />
        <div className="flex items-center gap-5">{button}</div>
      </div>

      <div className="flex flex-col md:flex-row w-full gap-32 mt-10">
        <div className="flex flex-col gap-10">
          <div className="skeleton h-7 w-44" />
          <div className="skeleton h-7 w-96" />
          <div className="skeleton h-7 w-96" />
          <div className="skeleton h-7 w-64" />
          <div className="skeleton h-7 w-96" />
          <div className="skeleton h-7 w-96" />
        </div>
        <div className="flex justify-end gap-10 w-full">
          <div className="skeleton h-[25rem] w-[40vw]" />
        </div>
      </div>
      <div className="skeleton h-7 w-1/6 mt-10" />
      <div className="skeleton h-10 w-4/6" />
      <div className="skeleton h-[30vh] w-full" />
    </div>
  )

  const integrationsContent = (
    <div className=" w-full flex flex-col gap-10">
      <div className="flex items-center justify-between">
        <div className="skeleton h-7 w-1/6" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <div className="skeleton h-[30vh] w-full" />
        <div className="skeleton h-[30vh] w-full" />
        <div className="skeleton h-[20vh] w-full" />
        <div className="skeleton h-[20vh] w-full" />
      </div>
    </div>
  )

  const billingContent = (
    <div className="w-full ">
      <div className=" w-full flex flex-col gap-10">
        <div className="skeleton h-7 w-2/6" />
        <div className="skeleton h-[25vh] w-full" />
        <div className="skeleton h-[25vh] w-full" />
        <div className="skeleton h-[30vh] w-full" />
        <div className="skeleton h-[40vh] w-full" />
      </div>
    </div>
  )

  const premiumContent = (
    <div className="w-full ">
      <div className=" w-full flex flex-col gap-10">
        <div className="skeleton h-7 w-2/6" />
        <div className="skeleton h-7 w-3/12" />
        <div className="skeleton h-7 w-2/12" />

        <div className="flex flex-wrap justify-center gap-10">
          <div className="w-[250px] h-[280px] skeleton" />
          <div className="w-[250px] h-[280px] skeleton" />
          <div className="w-[250px] h-[280px] skeleton" />
        </div>

        <div className="skeleton h-[40vh] w-full" />
        <div className="skeleton h-[25vh] w-full" />
        <div className="skeleton h-[30vh] w-full" />
      </div>
    </div>
  )

  const teamsFeaturesContent = (
    <div className="w-full ">
      <div className="grid grid-cols-12">
        <div className="col-span-12 md:col-start-2 md:col-span-10">
          <div className="skeleton h-12 w-6/12" />
          <div className="flex items-center flex-col ml:flex-row justify-between w-full mt-10 gap-5">
            <div className="w-full flex flex-col gap-10">
              <div className="skeleton h-7 w-10/12" />
              <div className="skeleton h-7 w-7/12" />
              <div className="skeleton h-7 w-8/12" />
              <div className="skeleton h-7 w-9/12" />
            </div>
            <div className="skeleton h-[20rem] w-10/12" />
          </div>
        </div>
      </div>
      <div className="w-full gap-10 grid grid-cols-12">
        <div className="col-span-12 lg:col-start-2 lg:col-span-8 mt-10 flex flex-col gap-5">
          <div className="skeleton h-7 w-1/12" />
          <div className="skeleton h-7 w-4/12" />
          <div className="flex flex-col gap-2">
            <div className="skeleton h-5 w-full" />
            <div className="skeleton h-5 w-10/12" />
            <span />
            <span />
            <div className="skeleton h-5 w-full" />
          </div>
        </div>
        <div className="col-span-12 lg:col-start-2 lg:col-span-8 flex flex-col gap-5">
          <div className="skeleton h-7 w-2/12" />
          <div className="flex flex-col gap-2">
            <div className="skeleton h-5 w-full" />
            <span />
            <span />
            <div className="skeleton h-5 w-full" />
            <div className="skeleton h-5 w-10/12" />
          </div>
        </div>
        <div className="col-span-12 lg:col-start-2 lg:col-span-8 flex flex-col gap-5">
          <div className="skeleton h-7 w-4/12" />
          <div className="flex flex-col gap-2">
            <div className="skeleton h-5 w-11/12" />
            <span />
            <span />
            <div className="skeleton h-5 w-10/12" />
          </div>
        </div>
        <div className="col-span-12 lg:col-start-2 lg:col-span-8 flex flex-col gap-5">
          <div className="skeleton h-7 w-3/12" />
          <div className="flex flex-col gap-2">
            <div className="skeleton h-5 w-full" />
            <span />
            <span />
            <div className="skeleton h-5 w-11/12" />
            <div className="skeleton h-5 w-10/12" />
          </div>
        </div>
      </div>
    </div>
  )

  const aboutYouContent = (
    <div className="w-full  mt-[30px] flex flex-col items-center gap-10">
      <div className="skeleton h-7 w-2/6" />
      <div className="skeleton h-5 w-1/6" />
      <div className="skeleton h-7 w-3/12" />
      <div className="flex flex-wrap justify-center gap-10">
        <div className="w-[250px] h-[300px] skeleton" />
        <div className="w-[250px] h-[300px] skeleton" />
      </div>
    </div>
  )

  const signUpContent = (
    <div className="grid grid-cols-12 ">
      <div className="col-start-1 col-span-12 md:col-start-3 md:col-span-8 lg:col-start-5 lg:col-span-4">
        <div className="w-full">
          <div className="w-full relative h-[12px]">
            <div className="flex flex-col items-left gap-5 absolute top-0 left-0">
              <div className="bg-dark-1 h-9 w-9 rounded-full" />
              <div className="bg-dark-1 h-5 w-28 rounded-md -ml-10" />
            </div>
            <div className="flex flex-col items-center gap-5 absolute top-0 left-1/2 right-1/2">
              <div className="bg-dark-1 h-9 w-9 rounded-full" />
              <div className="bg-dark-1 h-5 w-28 rounded-md" />
            </div>
            <div className="flex flex-col items-end gap-5 absolute top-0 right-0">
              <div className="bg-dark-1 h-9 w-9 rounded-full" />
              <div className="bg-dark-1 h-5 w-28 rounded-md -mr-10" />
            </div>
          </div>
          <div className="bg-dark-1 h-2 w-full" />

          <div className="w-full mt-32 flex flex-col items-center gap-8">
            <div className="skeleton h-7 w-2/3" />
            <div className="flex gap-2 mt-3 w-full flex-col items-center">
              <div className="skeleton h-4 w-2/3" />
              <div className="skeleton h-4 w-1/3" />
            </div>
            <div className="skeleton h-[40vh] w-full" />
          </div>
        </div>
      </div>
    </div>
  )

  const acceptInviteContent = (
    <div className="w-full grid place-items-center">
      <div className="skeleton h-[25vh] w-11/12" />
    </div>
  )
  const authContent = (
    <div className="h-[88vh] flex justify-around items-center">
      <div className="skeleton w-[500px] h-[440px]" />
    </div>
  )

  const memberTab = (
    <div className="h-[88vh] flex flex-col gap-5 px-6">
      {title}
      <div className="skeleton w-full h-[440px]" />
    </div>
  )

  const tripDetail = (
    <div className="flex flex-col gap-5 px-6">
      {title}
      <div className="skeleton w-full h-20"></div>
      <div className="skeleton w-full h-[250px]" />
      <div className="skeleton h-9 w-full" />
      <div className="skeleton h-9 w-full" />
      <div className="skeleton h-9 w-1/2" />
      <div className="skeleton h-9 w-1/3" />
      <div className="flex items-center gap-5">
        <div className="skeleton h-9 w-1/2" />
        <div className="skeleton h-9 w-1/2" />
      </div>
      <div className="skeleton h-9 w-full" />
      <div className="skeleton h-9 w-full" />
      <div className="skeleton h-9 w-full" />
      <div className="skeleton h-9 w-full" />
      <div className="skeleton h-9 w-full" />
      <div className="skeleton h-9 w-full" />
    </div>
  )

  const mergeImport = (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 items-center gap-2">
        <div className="skeleton h-[120px]"></div>
        <div className="skeleton h-[120px]"></div>
      </div>
      <div className="skeleton w-full h-80"></div>
    </div>
  )

  return (
    <div
      className={cn([
        'py-6 w-full grid grid-cols-12',
        isChildPage && 'px-[40px]',
        className ?? ''
      ])}
    >
      <div className="col-span-12 xxl:col-span-10 xxl:col-start-2">
        {variant === 'default' && defaultContent}

        {variant === 'settings' && settingsContent}

        {variant === 'home' && homeContent}

        {variant === 'team-home' && teamHomeContent}

        {variant === 'report' && reportContent}

        {variant === 'report-drawer' && reportDrawerContent}

        {variant === 'member-tab' && memberTab}

        {variant === 'trip-detail' && tripDetail}

        {variant === 'pay-period' && payPeriodContent}

        {variant === 'schedule' && scheduleContent}

        {variant === 'integrations' && integrationsContent}

        {variant === 'billing' && billingContent}

        {variant === 'premium' && premiumContent}

        {variant === 'teams-features' && teamsFeaturesContent}

        {variant === 'about-you' && aboutYouContent}

        {variant === 'sign-up' && signUpContent}

        {variant === 'accept-invite' && acceptInviteContent}

        {variant === 'merge-import' && mergeImport}

        {variant === 'auth' && authContent}

        {variant === 'app' && <AppLoadingIndicator />}
      </div>
    </div>
  )
}

export default Skeletons
